import { GlobalRequest } from '~/components/global-request'
import { GetServerSideProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import { SinglePost } from '~/components/business/SinglePost'
import { MyContainer } from '~/components/ui/MyContainer'
import {
  CacheValueProps,
  delScrollCache,
  getGraphData,
  getScrollCache,
  serverSideCatch,
  setScrollCache,
} from '~/utils'
import {
  GET_AI_SIZES,
  GET_USERINFO,
  USER_SQUAREPOST_LIST,
  GET_VISIBLE_LIST,
  GET_ARTWORK_MODELS,
  GET_STYLES_MODELS,
} from '~/graphqls/gqls'
import {
  getUserInfo,
  getUserInfo_userinfo,
  userPostPage,
  userPostPageVariables,
  PostTypes,
  PostSortField,
  SortType,
  PostUserQueryInput,
  getAISizes,
  SquarePostType,
  strToBoolMap,
  getVisibleList,
  getVisibleList_squareModule_postVisibleTypeList,
  getArtworkModels,
  getStyleModels,
} from '~/graphqls/types'
import { PAGE_SIZE } from '~/configs'
import { MyEmpty } from '~/components/ui/MyEmpty'
import useInfiniteScroll from '~/hooks/useInfiniteScroll'
import { Layout } from '~/layouts/default'

import { UserHeader } from '~/components/pages/user/UserHeader'
import { SEOHead } from '~/components/business/SEOHead'
import { useMasonry } from '~/hooks/useMasonry'
import { PlazaSort } from '~/components/pages/user/PlazaSort'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'
import useScrollTop from '~/hooks/useScrollTop'
import MyTabs from '~/components/ui/MyTabs'
import UpdateEngine from '~/components/business/SinglePost/UpdateEngine'

const UserDetail: NextPage<{
  postList: PostTypes[]
  userinfo: getUserInfo_userinfo
  hasNextPage: boolean
  sizes: { name: string }[]
  scrollCache: CacheValueProps
  visibleTypeList: getVisibleList_squareModule_postVisibleTypeList[]
  models: { name: string; ids: string[] }[]
  styleModels: { key: string; name: string }[]
}> = ({
  postList: _postList,
  userinfo,
  hasNextPage: _nextPage,
  sizes,
  scrollCache,
  visibleTypeList,
  models,
  styleModels,
}) => {
  const router = useRouter()
  const uid = Number(router.query.id as string)

  const tabsData = [
    {
      id: `/user/${uid}`,
      name: 'AI创作',
    },
    {
      id: `/user/${uid}/note`,
      name: '帖子',
    },
    {
      id: `/user/${uid}/comic`,
      name: '漫画',
    },
    {
      id: `/user/${uid}/collection`,
      name: '合集',
    },
  ]

  const scrollTop = useScrollTop()

  const [postList, setPostList] = useState(_postList)
  const [hasNextPage, setHasNextPage] = useState(_nextPage)

  const extraQueryParams: PostUserQueryInput = useMemo(() => {
    const params: PostUserQueryInput = {
      uid,
      sortField: router.query.sort as PostSortField,
      sort: SortType.DESC,
      type: SquarePostType.AI_ARTWORK,
    }

    if (!!router.query.model) {
      params.modelClassificationIds = (router.query.model as string)?.split(',')
    }

    if (!!router.query.styleModelKey) {
      params.styleModelKey = router.query.styleModelKey as string
    }

    if (!!router.query.size) {
      params.aiArtworkSize = router.query.size as string
    }
    if (router.query.initImage) {
      params.initImage = strToBoolMap[router.query.initImage as string]
    }
    if (router.query.character) {
      params.character = strToBoolMap[router.query.character as string]
    }
    if (router.query.modelFusion) {
      params.modelFusion = strToBoolMap[router.query.modelFusion as string]
    }

    if (router.query.visibleType) {
      params.visibleType = +router.query.visibleType
    }

    return params
  }, [uid, router.query])
  // 有其它筛选条件不显示置顶
  const showTop = useMemo(
    () =>
      !(
        (extraQueryParams.sortField &&
          extraQueryParams.sortField !== PostSortField.CREATE_TIME) ||
        // 画面尺寸
        extraQueryParams.aiArtworkModel ||
        // 模型选择
        extraQueryParams.aiArtworkSize ||
        // 风格模型
        extraQueryParams.styleModelKey ||
        // 参考图
        extraQueryParams.initImage ||
        // 公开范围
        extraQueryParams.visibleType !== undefined
      ),
    [extraQueryParams]
  )

  const { InfiniteScollComp, list } = useInfiniteScroll<
    PostTypes,
    userPostPage,
    userPostPageVariables
  >({
    queryDoc: USER_SQUAREPOST_LIST,
    initData: postList || [],
    initPageSize: PAGE_SIZE,
    hasNextPage: hasNextPage,
    extraQueryParams: {
      param: extraQueryParams,
    },
    hasNextPageExtractor: res => {
      return (
        res.data?.squareModule?.userPostPage?.pageInfo?.hasNextPage || false
      )
    },
    dataExtractor: res => {
      return getGraphData<PostTypes>(
        res.data?.squareModule?.userPostPage?.edges || []
      )
    },
  })

  const { columnList, columnWidth, columnGutter } = useMasonry<PostTypes>({
    data: list,
    sizeLoader: (it: PostTypes) => {
      if (!it?.coverImage?.height || !it?.coverImage?.width) return 1
      return it?.coverImage?.height / it?.coverImage?.width
    },
    itemExtraHeight: 56,
  })

  const onTabsChange = useCallback(
    (item: any) => {
      /** 为了在tab切换时，不保留滚动位置 */
      delScrollCache({
        key: item.id,
      })
      router.replace(item.id)
    },
    [uid, router]
  )

  useDidUpdateEffect(() => {
    setPostList(_postList)
    setHasNextPage(_nextPage)
  }, [router.query])

  useEffect(() => {
    window.scrollTo({ top: scrollCache.scrollTop || 0, behavior: 'auto' })
  }, [])

  useDidUpdateEffect(() => {
    setScrollCache({
      key: router.asPath,
      value: {
        path: router.asPath,
        scrollTop,
        pageSize: list.length,
      },
    })
  }, [list.length, scrollTop, router.asPath])

  return (
    <Layout>
      <SEOHead
        title={userinfo?.nickname + ' - 个人主页'}
        desc={userinfo?.personalizedSignature || '这个人很懒，什么都没有留下'}
      />
      <MyContainer>
        <UserHeader userinfo={userinfo} />
        <MyTabs
          className="mb-3 text-base"
          items={tabsData}
          onChange={onTabsChange}
          active={tabsData[0].id}
        />
        <div className="h-[1px] bg-neutral-300 mb-4"></div>
        <PlazaSort
          sizes={sizes}
          visibleTypeList={visibleTypeList}
          models={models}
          styleModels={styleModels}
        />
        {list.length > 0 ? (
          <>
            <div className="flex justify-between items-start">
              {columnList?.map((item, index) => (
                <div
                  style={{
                    width: columnWidth,
                  }}
                  key={index}
                >
                  {item?.map(it => (
                    <div
                      key={it.id}
                      style={{
                        marginBottom: columnGutter,
                      }}
                    >
                      <SinglePost
                        post={it}
                        showSticky={showTop && it.top}
                        showProcessingTag
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {InfiniteScollComp}
          </>
        ) : (
          <MyEmpty />
        )}
      </MyContainer>
      {/* 一键同款的轮询状态 */}
      <UpdateEngine />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  return await serverSideCatch(async () => {
    const { initialState, client } = await GlobalRequest(context)
    const userId = Number(context.params?.id)

    if (Number.isNaN(userId)) {
      return {
        notFound: true,
      }
    }

    const scrollCache = getScrollCache(context)

    const extraQueryParams: PostUserQueryInput = {
      uid: userId,
      sort: SortType.DESC,
      type: SquarePostType.AI_ARTWORK,
    }

    if (context.query.model) {
      extraQueryParams.modelClassificationIds = (
        context.query.model as string
      ).split(',')
    }

    if (context.query.styleModelKey) {
      extraQueryParams.styleModelKey = context.query.styleModelKey as string
    }

    if (context.query.size) {
      extraQueryParams.aiArtworkSize = context.query.size as string
    }

    if (context.query.sort) {
      extraQueryParams.sortField = context.query.sort as PostSortField
    }

    if (context.query.initImage) {
      extraQueryParams.initImage =
        strToBoolMap[context.query.initImage as string]
    }
    if (context.query.character) {
      extraQueryParams.character =
        strToBoolMap[context.query.character as string]
    }
    if (context.query.modelFusion) {
      extraQueryParams.modelFusion =
        strToBoolMap[context.query.modelFusion as string]
    }

    if (context.query.visibleType) {
      extraQueryParams.visibleType = +context.query.visibleType
    }

    const [postRes, userinfoRes, sizeRes, visibleRes, modelRes, styleModelRes] =
      await Promise.all([
        client.query<userPostPage, userPostPageVariables>({
          query: USER_SQUAREPOST_LIST,
          variables: {
            offset: 0,
            first: scrollCache.pageSize || PAGE_SIZE,
            param: extraQueryParams,
          },
          fetchPolicy: 'no-cache',
        }),
        client.query<getUserInfo>({
          query: GET_USERINFO,
          variables: {
            uid: userId,
          },
          fetchPolicy: 'no-cache',
        }),
        client.query<getAISizes>({
          query: GET_AI_SIZES,
          fetchPolicy: 'no-cache',
        }),
        client.query<getVisibleList>({
          query: GET_VISIBLE_LIST,
          fetchPolicy: 'no-cache',
        }),
        client.query<getArtworkModels>({
          query: GET_ARTWORK_MODELS,
          fetchPolicy: 'no-cache',
        }),
        client.query<getStyleModels>({
          query: GET_STYLES_MODELS,
          fetchPolicy: 'no-cache',
        }),
      ])
    return {
      props: {
        initialState,
        scrollCache,
        postList: getGraphData(
          postRes?.data?.squareModule?.userPostPage?.edges || []
        ),
        hasNextPage:
          postRes?.data?.squareModule?.userPostPage?.pageInfo?.hasNextPage ||
          false,
        userinfo: userinfoRes?.data?.userinfo,
        sizes:
          sizeRes?.data?.pictureGraph?.aiCreateArtworkOptionMenu?.size || [],
        visibleTypeList:
          visibleRes?.data?.squareModule?.postVisibleTypeList || [],
        models:
          modelRes?.data?.aiArtworkResourceModule?.modelClassificationList ||
          [],
        styleModels: styleModelRes?.data?.pictureGraph?.styleModelList || [],
      },
    }
  })
}

export default UserDetail
